import { createContext, type FC } from "react";

import { useHistory, useParams } from "react-router";

import { Button, Icon, Page, Section } from "@alpacahq/ui/old";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetOAuthClientById,
  useGetOAuthConnectedClients,
  useGetOAuthCreatedClients,
} from "~/v2/api/hooks";
import { FullScreenSpinner } from "~/v2/components/common";
import type { OAuthClientByIdResponse } from "../../api/oauth";
import { Path } from "../../path";
import { AppDetailsAbout } from "./AppDetailsAbout";
import { AppDetailsCategories } from "./AppDetailsCategories";
import { AppDetailsEdit } from "./AppDetailsEdit";
import { AppDetailsHeader } from "./AppDetailsHeader";
import { AppDetailsPermissions } from "./AppDetailsPermissions";
import { AppDetailsRevoke } from "./AppDetailsRevoke";
import { AppDetailsScreenshots } from "./AppDetailsScreenshots";

export interface IAppDetailContext {
  client: OAuthClientByIdResponse;
}

export const AppDetailsContext = createContext<IAppDetailContext>({
  // this is a workaround to avoid null checks
  client: {} as OAuthClientByIdResponse,
});

export const AppDetailsPage: FC = () => {
  const history = useHistory();
  const { clientId } = useParams<{ clientId: string }>();
  const id = clientId || "";

  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();
  const { client } = useGetOAuthClientById({
    clientId: id,
    options: { enabled: !!id },
  });

  // get the list of clients connected to the current account
  const { clients: connectedClients } = useGetOAuthConnectedClients({
    accountId: account?.id,
    options: {
      enabled: !!account?.id,
    },
  });

  // get the list of clients created by the current user
  const { clients: createdClients } = useGetOAuthCreatedClients();

  // is the current client in the connected clients list?
  const isInConnectedList = connectedClients?.some((c) => c.client_id === id);

  // is the current client in the created clients list?
  const isInCreatedList = createdClients?.some((c) => c.client_id === id);

  if (!client) {
    return <FullScreenSpinner />;
  }

  return (
    <AppDetailsContext.Provider value={{ client }}>
      <Page
        title="Alpaca Connect"
        subtitle="Integrate apps built by the community."
      >
        <div className="flex justify-between space-x-2">
          <Button
            variant="alternate"
            leftIcon={<Icon name="ArrowLeftCircle" className="mr-2" />}
            onClick={() => history.push(Path.ROUTE_ALPACA_CONNECT)}
          >
            Back to Apps
          </Button>
          <div className="flex space-x-2">
            {isInCreatedList && <AppDetailsEdit />}
            {isInConnectedList && <AppDetailsRevoke />}
          </div>
        </div>
        <div className="gap-6 lg:grid lg:grid-cols-3">
          <div className="col-span-2">
            <AppDetailsHeader />
            <AppDetailsAbout />
            <AppDetailsScreenshots />
          </div>
          <div className="col-span-1 lg:-mt-12">
            <Section title="Status">
              <b>
                {client.status === "DISABLED" ? "Not Published" : "Published"}
              </b>
            </Section>
            <AppDetailsCategories />
            <AppDetailsPermissions />
          </div>
        </div>
      </Page>
    </AppDetailsContext.Provider>
  );
};

export default AppDetailsPage;
